import type { LevelDef } from './types';

/**
 * Day 2 — Tuesday, rain. A late shift in the covered shotengai. The shops pull
 * their shutters down one by one as she walks, so the light disappears behind
 * her. Nobody follows her yet, but a man in a black mask stands in a side
 * passage of the arcade and watches her pass, and the news says someone was
 * followed from her station on Friday night.
 */
export const night2: LevelDef = {
  id: 'night2',
  night: 2,
  title: 'Shotengai',
  subtitle: 'Tuesday · 22:58 · rain',
  look: 'rain',
  card: ['Late shift at the bookshop.', 'The arcade is closing for the night.', 'It has rained all day. The streets are empty.'],
  calm: true,
  outfit: 'work',
  clockStart: 22 * 60 + 58,
  map: [
    '#########################', // 0
    '################H########', // 1
    '################.########', // 2
    '########.........########', // 3
    '########.#########.######', // 4
    '########.#########.A#####', // 5
    '########B#########L######', // 6
    '########.#########.######', // 7
    '########.#########.######', // 8
    '###.........L....T....###', // 9
    '###.#################.###', // 10
    '###.#################.###', // 11
    '###.#################L###', // 12
    '###.#################.###', // 13
    '###.#################V###', // 14
    '###....V.....########.###', // 15
    '###.########.########L###', // 16
    '###.########.########.###', // 17
    '###.########X########.###', // 18
    '###.########.########.###', // 19
    '###F########.########L###', // 20
    '###.########.########.###', // 21
    '###...######.########.###', // 22
    '###.#.######.C#######.###', // 23
    '###.#.######L########L###', // 24
    '###.#.######.########.###', // 25
    '###.#.######.########.###', // 26
    '###.....L.......L.....###', // 27
    '#####.######.############', // 28
    '#####F######.############', // 29
    '#####.######R############', // 30
    '#####.######Y############', // 31
    '#####.######R############', // 32
    '#####.######Q############', // 33
    '#####.######Y############', // 34
    '#####.......R############', // 35
    '############W############', // 36
    '############Y############', // 37
    '############Q############', // 38
    '############R############', // 39
    '############Y############', // 40
    '############Q############', // 41
    '############RRRYRQRQ#####', // 42
    '############Y############', // 43
    '############Q############', // 44
    '############R############', // 45
    '############Y############', // 46
    '############R############', // 47
    '############Q############', // 48
    '############Y############', // 49
    '############R############', // 50
    '############S############', // 51
    '############.############', // 52
    '############P############', // 53
  ],
  // Nobody follows her yet, so the light rules don't apply.
  zones: [{ zone: 1 }],
  battery: 24,
  pace: { tension: 0.6, patience: 720 },
  weather: 'rain',
  theme: {
    sky: '#06080d',
    fog: { color: '#0c1017', density: 0.042 },
    hemi: { sky: '#3e4c66', ground: '#08090a', intensity: 0.6 },
    ambient: 0.45,
    moon: 0.25,
    asphalt: '#1f2124',
    wet: true,
    overcast: true,
    walls: ['#7d776d', '#8a8378', '#6f6c66', '#938a7c', '#77736c'],
  },
  stranger: { walkMax: 2.85, chaseSpeed: 3.05, gapMax: 37, spawnDelay: 7 },
  lines: {
    intro: 'The arcade first. At least it\'s dry in there.',
    shutter: 'They\'re closing up. It\'s getting darker in here.',
    arcadeEnd: 'Rain. I can\'t hear anything over it.',
    rainEars: 'Somebody could walk right behind me and I wouldn\'t hear a thing.',
    sideLane: 'A back lane. Out of the arcade, into the dark.',
    deadend: 'All closed. Everything down here is closed.',
    roadworks: 'Flooded. The drain\'s overflowing — I can\'t get through.',
    konbini: 'The konbini. Bright… but the road past it is flooded.',
    wrongBuilding: 'Not mine. Wrong building.',
    homeStretch: 'Nearly there. Keys. Keys.',
    sensor: 'A sensor light. Made me jump.',
    momIgnored: 'I\'ll text her back in a minute.',
    momIgnoredAgain: 'Mom again. I\'ll answer when I\'m in.',
    firstSight: 'Someone in the side passage. Just… standing there.',
    watched: 'He didn\'t move. He just watched me walk past.',
  },
  endings: {
    home: {
      title: 'Home.',
      lines: ['She shakes out her umbrella by the door.', 'On the TV, the news is still talking about Yoruno Station.', 'She checks the lock. Twice.'],
      hint: 'Next: Wednesday. The station road, after dark.',
    },
  },
  mom: {
    first: { node: 'start', at: 8 },
    script: {
      start: {
        text: "Are you still at work? It's pouring.",
        options: [
          { text: 'Just left, walking home', end: 'Ok. Borrow an umbrella from the shop!' },
          { text: 'Yeah, closing up', next: 'late' },
        ],
      },
      late: {
        text: 'So late again. Do they at least pay you extra?',
        options: [
          { text: 'Ha. No.', end: "Hmm. Text me when you're walking." },
          { text: "It's fine, I like it", end: 'Ok. Come home safe.' },
        ],
      },
      rain: {
        text: 'Did you see the news? A woman was followed from your station on Friday.',
        options: [
          { text: "That's so scary", next: 'news' },
          { text: "I'm fine Mom", end: 'I know. I worry anyway.' },
        ],
      },
      news: {
        text: 'Take the main road home, ok? Not the back lanes.',
        options: [
          { text: 'Ok', end: 'Good.' },
          { text: 'The back lanes are shorter', end: 'Not tonight. Please.' },
        ],
      },
    },
  },
  onEnterCell(ctx, cell) {
    const key = `${cell.c},${cell.r}`;
    if (key === '12,28') ctx.sayOnce('arcadeEnd');
    if (key === '5,35') ctx.sayOnce('sideLane');
    if (key === '13,23' || key === '12,23') ctx.sayOnce('konbini');
    if (key === '12,19') ctx.sayOnce('roadworks');
    if (key === '19,42') ctx.sayOnce('deadend');
    // Halfway up the arcade, a man stands in the side passage and watches her go by. He doesn't follow. Not tonight.
    if (cell.c === 12 && cell.r >= 43 && cell.r <= 47 && !ctx.said('watcherSet') && ctx.strangerWatches(15, 42)) ctx.sayOnce('watcherSet');
    if (cell.r === 37 && ctx.said('firstSight')) ctx.sayOnce('watched');
    // Out of the arcade and into the rain: the news, then Mom.
    if (cell.r === 27 && !ctx.said('rainEars')) {
      ctx.sayLater('rainEars', 3);
      ctx.notify('news2', 'News', 'Woman followed from Yoruno Station on Friday night. Police ask for witnesses.', 'system', 6);
      ctx.momLater('rain', 16);
    }
    if (cell.r === 3) ctx.sayOnce('homeStretch');
  },
};
